import { BehaviorSubject } from 'rxjs';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { ErrorService } from '../../../../services/error.service';
import { API, Pagination, RoleDTO } from '../../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Mutable } from '../../../../services/global/global-interfaces';

@Component({
  selector: 'sb-role-edit',
  templateUrl: './role-edit-modal.component.html',
  styleUrls: ['./role-edit-modal.component.scss'],
  standalone: false,
})
export class RoleEditModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  roleId: number;
  loading: boolean;
  role: Mutable<RoleDTO>;
  personalPermissions: string[];
  displayedPermissions: string[];

  tempInheritedRoles: RoleDTO[];

  roles: RoleDTO[];
  roleSearch: string;
  debounceTimer: ReturnType<typeof setTimeout>;
  formRoles: UntypedFormGroup;
  rolePagination: BehaviorSubject<Pagination>;
  collectionSizeRole: number;
  maxSizeRole: number;
  resultCountRole: number;
  rolesLoading: boolean;
  permissionSearch: string;
  transSub;
  form = new UntypedFormGroup({
    Title: new UntypedFormControl('', Validators.required),
  });
  formPermissions = new UntypedFormGroup({});
  isCustomRole: boolean;

  constructor(
    private readonly api: API,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService,
    private readonly modalNavigation: ModalNavigationService
  ) {
    this.loading = true;
    this.isCustomRole = true;
    this.formRoles = new UntypedFormGroup({});
    this.tempInheritedRoles = [];
    this._pageRole = 1;
    this.maxSizeRole = 300;
    this.debounceTimer = null;
    this.rolePagination = new BehaviorSubject<Pagination>({
      page: this.pageRole ? this.pageRole : 1,
      count: this.maxSizeRole ? this.maxSizeRole : 8,
      pages: -1,
      resultcount: -1,
    });
    this.collectionSizeRole = -1;
    this.resultCountRole = -1;
    this.rolesLoading = true;
  }

  _pageRole: number;

  get pageRole(): number {
    return this._pageRole;
  }

  set pageRole(val: number) {
    this._pageRole = val;
    this.rolePagination.value.page = val;
  }

  get Title() {
    return this.form.get('Title');
  }

  ngOnInit(): void {
    this.loading = true;

    // wait for modal animation
    setTimeout(() => {
      this.roleId = +this.modalNavigation.getCurrentNavigation().data['roleID'];
      if (isNaN(this.roleId)) {
        this.roleId = undefined;
      }
      this.refreshFromServer();
      this.initRolePagination();
    }, 300);
  }

  checkIfSelected(role: RoleDTO): boolean {
    for (const r of this.tempInheritedRoles) {
      if (r.id === role.id) {
        return true;
      }
    }
  }

  initRolePagination() {
    this.rolePagination.subscribe((e: Pagination) => {
      this.collectionSizeRole = e.resultcount;
      this.maxSizeRole = e.count;
      this.resultCountRole = e.resultcount;
    });

    this.requestRolesDebounced();
  }

  requestRolesDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.requestRoles();
    }, 500);
  }

  roleSearchChanged($event: string) {
    this.roleSearch = $event;
    this.requestRolesDebounced();
  }

  requestRoles() {
    this.api
      .ca_am_Role_retrieveAll(
        {
          search: this.util.encodeSearchQueryParam(false, this.roleSearch),
        },
        this.rolePagination
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.rolesLoading = false;
        })
      )
      .subscribe({
        next: (data: RoleDTO[]) => {
          if (this.roles) {
            for (const role of this.roles) {
              this.formRoles.removeControl(role.name);
            }
          }
          this.roles = data;
          this.roles.forEach(r => {
            let inheritedRole = false;
            this.role.inheritedRoles?.forEach(ur => {
              if (r.name === ur.name) {
                inheritedRole = true;
              }
            });
            this.formRoles.addControl(r.name, new UntypedFormControl(inheritedRole));
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  onPermissionSearch($event: string) {
    this.permissionSearch = $event;
    this.refreshDisplayedPermissions();
  }

  refreshDisplayedPermissions() {
    this.displayedPermissions = this.filteredPermissions();
  }

  filteredPermissions(): string[] {
    if (this.personalPermissions) {
      if (this.permissionSearch) {
        const ret: string[] = [];
        for (const perm of this.personalPermissions) {
          const translated: string = this.translate.instant('permission.' + perm);
          if (
            perm === this.permissionSearch ||
            translated.toLowerCase() === this.permissionSearch.toLowerCase() ||
            perm.toLowerCase().includes(this.permissionSearch.toLowerCase()) ||
            translated.toLowerCase().includes(this.permissionSearch.toLowerCase())
          ) {
            ret.push(perm);
          }
        }

        return ret;
      }
    }
    return this.personalPermissions;
  }

  refreshFromServer() {
    this.loading = true;
    if (!this.roleId) {
      this.isCustomRole = true;
      this.role = new RoleDTO();
      this.role.explicitPermissions = [];
      this.getPermissionSettings();
    } else {
      this.isCustomRole = true;
      this.api
        .ca_am_Role_retrieveById(this.roleId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: RoleDTO) => {
            this.role = data;
            this.isCustomRole = this.role.custom;
            this.tempInheritedRoles = data.inheritedRoles;
            this.getPermissionSettings();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  getPermissionSettings() {
    this.api
      .shared_Client_retrievePersonalPermissions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: string[]) => {
          this.personalPermissions = data;
          this.personalPermissions.forEach(s => {
            this.formPermissions.addControl(s, new UntypedFormControl(this.role.explicitPermissions.includes(s)));
          });

          this.refreshDisplayedPermissions();

          if (this.transSub) {
            try {
              this.transSub.unsubscribe();
            } catch (err) {}
            this.transSub = null;
          }

          this.transSub = this.translate.onDefaultLangChange.subscribe(() => {
            this.refreshDisplayedPermissions();
          });

          if (!this.isCustomRole) {
            this.form.disable();
            this.formRoles.disable();
            this.formPermissions.disable();
          }
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  updateRolePermissions(permission: string, value: boolean) {
    if (!this.role || !permission) {
      return;
    }
    if (value === true) {
      this.role.explicitPermissions = this.role.explicitPermissions.filter(e => e !== permission);
      this.role.explicitPermissions.push(permission);
    } else {
      this.role.explicitPermissions = this.role.explicitPermissions.filter(e => e !== permission);
    }
    this.previewRole();
  }

  updateRoleInheritance(selectedRole: RoleDTO) {
    if (!this.tempInheritedRoles || !selectedRole) {
      return;
    }
    for (const [index, role] of this.tempInheritedRoles.entries()) {
      if (role.id === selectedRole.id) {
        this.tempInheritedRoles.splice(index, 1);
        this.previewRole();
        return;
      }
    }
    this.tempInheritedRoles.push(selectedRole);
    this.previewRole();
  }

  previewRole() {
    if (this.isCustomRole) {
      if (!this.roleId || !this.role.id) {
        const rolePreview = { ...this.role };
        let emptyName = false;
        rolePreview.inheritedRoles = this.tempInheritedRoles;
        if (!this.role.name) {
          emptyName = true;
          rolePreview.name = 'tempRole';
        }
        this.api
          .ca_am_Role_createRolePreview(rolePreview)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (data: RoleDTO) => {
              if (emptyName) {
                delete data.name;
              }
              this.role = data;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.role.inheritedRoles = this.tempInheritedRoles;
        this.api
          .ca_am_Role_editRolePreview(this.role)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (data: RoleDTO) => {
              this.role = data;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  updateRole() {
    if (this.isCustomRole) {
      this.role.inheritedRoles = this.tempInheritedRoles;
      if (!this.roleId || !this.role.id) {
        this.api
          .ca_am_Role_createRole(this.role)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (data: RoleDTO) => {
              this.role = data;
              this.close();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      } else {
        this.api
          .ca_am_Role_editRole(this.role)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (data: RoleDTO) => {
              this.role = data;
              this.close();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  setRoleInfo(attribute: string, value: string) {
    if (!this.role || !value) {
      return;
    }
    if (attribute === 'name') {
      this.role.name = value;
    }
  }

  hasEffectivePermission(val: string) {
    return this.role?.effectivePermissions?.includes(val);
  }

  selectAllPermissions() {
    this.role.explicitPermissions = this.displayedPermissions;
    this.previewRole();
  }

  selectNoPermissions() {
    this.role.explicitPermissions = [];
    this.previewRole();
  }

  close(success: boolean = true) {
    this.modalNavigation.result(success);
  }
}
